import { effect, Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';

const THEME_STORAGE_KEY = 'design-system-theme';
const THEME_ATTRIBUTE = 'data-theme';

@Injectable({
  providedIn: 'root',
})
export class DarkModeService {
  darkModeOn = signal<boolean>(this.getInitialDarkMode());

  constructor() {
    // Apply immediately so document theme is correct before the first async effect run
    this.applyTheme(this.darkModeOn());

    if (environment.enableDarkmode && typeof window !== 'undefined' && window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
        if (!this.hasStoredPreference()) {
          this.darkModeOn.set(event.matches);
        }
      });
    }

    if (environment.enableDarkmode && typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key !== THEME_STORAGE_KEY) return;
        if (event.newValue === 'dark' || event.newValue === 'light') {
          this.darkModeOn.set(event.newValue === 'dark');
          return;
        }
        // Preference cleared in another tab — fall back to system preference
        this.darkModeOn.set(
          typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
        );
      });
    }

    effect(() => {
      this.applyTheme(this.darkModeOn());
    });
  }

  toggleState(): void {
    this.darkModeOn.update((v) => !v);
    this.persistPreference(this.darkModeOn());
  }

  private getInitialDarkMode(): boolean {
    if (!environment.enableDarkmode) {
      return false;
    }
    const stored = this.getStoredPreference();
    if (stored !== null) {
      return stored === 'dark';
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return true;
    }
    return false;
  }

  private getStoredPreference(): 'light' | 'dark' | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch {
      return null;
    }
  }

  private hasStoredPreference(): boolean {
    return this.getStoredPreference() !== null;
  }

  private persistPreference(darkOn: boolean): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, darkOn ? 'dark' : 'light');
    } catch {
      // Ignore storage errors (e.g. private browsing, quota exceeded)
    }
  }

  private applyTheme(darkOn: boolean): void {
    if (typeof document === 'undefined') return;
    if (!environment.enableDarkmode) return;

    const html = document.documentElement;
    if (darkOn) {
      html.setAttribute(THEME_ATTRIBUTE, 'dark');
      html.classList.add('dark');
    } else {
      html.removeAttribute(THEME_ATTRIBUTE);
      html.classList.remove('dark');
    }
  }
}
